"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { HaekchenSymbol } from "./symbole";
import { UntermenueFehler, useUntermenueOffenBeiFehler } from "./untermenue-fehler";

/**
 * Untermenü „Darstellung“ im Konto-Menü: Flächenstil und Schriftgröße der eigenen Person.
 * Eintragsmarkup wie die Untermenü-Einträge des Blocks account-menu-with-status-controls.
 *
 * Die Wahl wirkt sofort (Attribute am <html>, die das Stylesheet auswertet), wird dann gespeichert
 * (POST an den Endpunkt, gilt pro Person) und der Seitenrahmen neu geladen, damit der Server dieselben
 * Attribute setzt. Schlägt das Speichern fehl, kehrt die Anzeige zum gespeicherten Wert zurück und ein
 * Satz nennt es (Fehlschlag, wiederholbar), nie ein Code. Preline schließt das Untermenü mit dem Klick;
 * bei Fehlschlag wird es wieder geöffnet, die Meldung (Alert „warnung“) überlebt so den Klick (r98).
 */
export interface DarstellungOption {
  wert: string;
  label: string;
}

export interface DarstellungAuswahl {
  flaechenstil: string;
  schriftgroesse: string;
}

const EINTRAG =
  "relative w-full flex items-center gap-x-2 py-2 px-3 text-start rounded-lg text-sm text-dropdown-item-foreground hover:bg-dropdown-item-hover focus:outline-hidden focus:bg-dropdown-item-focus";

const ATTRIBUT = { flaechenstil: "data-flaeche", schriftgroesse: "data-schrift" } as const;

export function DarstellungWahl({
  aktuell,
  flaechenstile,
  schriftgroessen,
  endpunkt,
}: {
  aktuell: DarstellungAuswahl;
  flaechenstile: readonly DarstellungOption[];
  schriftgroessen: readonly DarstellungOption[];
  endpunkt: string;
}) {
  const router = useRouter();
  const [wahl, setWahl] = useState<DarstellungAuswahl>(aktuell);
  const [fehler, setFehler] = useState(false);
  const [, start] = useTransition();
  // Preline schließt das Untermenü beim Klick; bei Fehlschlag wieder öffnen, damit die Meldung sichtbar bleibt (r98).
  const ankerRef = useUntermenueOffenBeiFehler<HTMLDivElement>(fehler);

  function setzeAmDokument(w: DarstellungAuswahl) {
    document.documentElement.setAttribute(ATTRIBUT.flaechenstil, w.flaechenstil);
    document.documentElement.setAttribute(ATTRIBUT.schriftgroesse, w.schriftgroesse);
  }

  function waehle(feld: keyof DarstellungAuswahl, wert: string) {
    if (wahl[feld] === wert) return;
    const vorher = wahl;
    const neu = { ...wahl, [feld]: wert };
    setFehler(false);
    setWahl(neu);
    setzeAmDokument(neu);
    start(async () => {
      try {
        const antwort = await fetch(endpunkt, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ [feld]: wert }),
        });
        if (!antwort.ok) throw new Error("nicht gespeichert");
        router.refresh();
      } catch {
        setWahl(vorher);
        setzeAmDokument(vorher);
        setFehler(true);
      }
    });
  }

  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-account-and-workspace-menus/account-menu-with-status-controls (Untermenü-Einträge, Wahl mit Häkchen) */}
      {/* `.group` je Auswahl: Preline-HSDropdown sucht beim Klick auf ein menuitemradio per closest(".group") die Geschwister und setzt aria-checked; ohne Gruppe wirft es (Befund N1). */}
      <div className="group" ref={ankerRef}>
        <p className="py-1.5 px-3 text-xs text-muted-foreground-1">Flächenstil</p>
        {flaechenstile.map((o) => (
          <button
            key={o.wert}
            type="button"
            role="menuitemradio"
            aria-checked={wahl.flaechenstil === o.wert}
            className={EINTRAG}
            onClick={() => waehle("flaechenstil", o.wert)}
          >
            {o.label}
            {wahl.flaechenstil === o.wert && <HaekchenSymbol />}
          </button>
        ))}
      </div>
      <div className="group pt-1 mt-1 border-t border-dropdown-divider">
        <p className="py-1.5 px-3 text-xs text-muted-foreground-1">Schriftgröße</p>
        {schriftgroessen.map((o) => (
          <button
            key={o.wert}
            type="button"
            role="menuitemradio"
            aria-checked={wahl.schriftgroesse === o.wert}
            className={EINTRAG}
            onClick={() => waehle("schriftgroesse", o.wert)}
          >
            {o.label}
            {wahl.schriftgroesse === o.wert && <HaekchenSymbol />}
          </button>
        ))}
      </div>
      {fehler && (
        <UntermenueFehler>Die Darstellung konnte nicht gespeichert werden. Bitte erneut versuchen.</UntermenueFehler>
      )}
    </>
  );
}
